import { UrlHandleString, User } from "kreate-common/modules/business-types";
import { getResourceKey$GetUser } from "kreate-server/commands/GetUser/fetcher";
import { handler$GetUser } from "kreate-server/commands/GetUser/handler";
import { getResourceKey$LoginStatus } from "kreate-server/commands/LoginStatus/fetcher";
import { handler$LoginStatus } from "kreate-server/commands/LoginStatus/handler";
import { LoginStatus$Result } from "kreate-server/commands/LoginStatus/typing";
import { GetServerSideProps } from "next";
import { SWRConfig, unstable_serialize } from "swr";

import PageProfile from "../../../containers/PageProfile";
import {
  formatFallback,
  intentionallyIgnoreError,
  omitUndefinedRecursively,
} from "../../../utils";

import { getDb } from "@/modules/mongodb";

type Props = {
  userId: string;
  user: User | undefined;
  loginStatus$Result: LoginStatus$Result | undefined;
};

export default function Route({ userId, user, loginStatus$Result }: Props) {
  return (
    <SWRConfig
      value={{
        fallback: formatFallback({
          [unstable_serialize(getResourceKey$GetUser({ userId }))]: user,
          [unstable_serialize(getResourceKey$LoginStatus())]:
            loginStatus$Result,
        }),
      }}
    >
      <PageProfile userId={userId} />
    </SWRConfig>
  );
}

/*
  NOTE: @kien-kreate: What we are doing here is a bit different from other routes:
  1. Let `handle = context.query.handle`
  2. Convert `handle` to `userId`
  3. Render `PageProfile` using `userId`

  In other routes, we only use data from DB to provide fallback data
  (initial data). However, in this route, when we convert `handle` to
  `userId`, we technically use the data from DB for logic.

  Note that this solution breaks the usual pattern. Although we have not
  foreseen any bad consequences, we should not consider this solution as
  a good example to follow. Let's observe for a considerable amount of time
  before we can conclude.
*/
export const getServerSideProps: GetServerSideProps<Props> = async (
  context
) => {
  const handleSP = UrlHandleString.safeParse(context.query.handle);
  if (!handleSP.success) {
    return { notFound: true };
  }

  const handle = handleSP.data;

  const db = await getDb();
  const user = await handler$GetUser(db, { handle }).catch(
    intentionallyIgnoreError
  );
  if (!user) {
    return { notFound: true };
  }
  const userId = user.userId;

  const loginStatus$Result = await handler$LoginStatus(db, {
    token: context.req.cookies["token"],
  }).catch(intentionallyIgnoreError);

  return {
    props: omitUndefinedRecursively({ userId, user, loginStatus$Result }),
  };
};
